import {
  EntityDeleteAction,
  EntityRemoveSubscriptionIdsAction,
  EntitySnapshotBeginAction,
  EntitySnapshotEndAction,
  EntityType,
  EntityUpdateAction,
  EntityUpdateUnion,
  EventResponseAction,
  ServerActionTypes
} from 'api'
import { RepositoryActions, RepositoryActionTypes, RepositoryActionUpdateField } from './repositoryActions'
import { EntityStore, RepositoryEntry } from './repositoryTypes'
import { isMajorVersionBump } from 'utils'

export type EntityReducerActions =
  | RepositoryActions
  | EntitySnapshotBeginAction
  | EntitySnapshotEndAction
  | EntityUpdateAction
  | EntityDeleteAction
  | EntityRemoveSubscriptionIdsAction
  | EventResponseAction

export const entityStoreInitialState = {}

export class EntityReducer {
  private snapshotInternalState: Record<string, EntityStore<EntityUpdateUnion>> = {}

  resetSnapshotInternalState() {
    this.snapshotInternalState = {}
  }

  run(
    state: EntityStore<EntityUpdateUnion>,
    action: EntityReducerActions,
    key: string = '',
    keepStateOnSnapshotBegin: boolean = false
  ): EntityStore<EntityUpdateUnion> {
    switch (action.type) {
      case ServerActionTypes.ENTITY_SNAPSHOT_BEGIN: {
        this.snapshotInternalState[`${action.payload.entityType}${key}`] = {}
        return keepStateOnSnapshotBegin ? state : entityStoreInitialState
      }

      case ServerActionTypes.ENTITY_SNAPSHOT_END: {
        if (this.snapshotInternalState[`${action.payload.entityType}${key}`]) {
          const temporaryEntityInternalState = { ...this.snapshotInternalState[`${action.payload.entityType}${key}`] }
          delete this.snapshotInternalState[`${action.payload.entityType}${key}`]
          return temporaryEntityInternalState
        }
        return state
      }

      case ServerActionTypes.ENTITY_UPDATE: {
        const { update } = action.payload

        const existing = state[update.entityId]

        const metaUpdates = existing ? existing.metaUpdates : []
        const updatedMetaUpdates =
          existing && this.shouldClearNonPersistentMeta(existing.lastUpdate, update)
            ? metaUpdates.filter(({ meta }) => meta.persist)
            : metaUpdates

        if (this.snapshotInternalState[`${update.entityType}${key}`]) {
          this.snapshotInternalState[`${update.entityType}${key}`][update.entityId] = {
            entityId: update.entityId,
            lastUpdate: update,
            fieldUpdates: [],
            metaUpdates: updatedMetaUpdates
          }
          return state
        }

        return {
          ...state,
          [update.entityId]: {
            entityId: update.entityId,
            lastUpdate: update,
            fieldUpdates: [],
            metaUpdates: updatedMetaUpdates
          }
        }
      }

      case ServerActionTypes.ENTITY_DELETE: {
        const { entityId } = action.payload

        if (this.snapshotInternalState[`${action.payload.entityType}${key}`]?.[entityId]) {
          delete this.snapshotInternalState[`${action.payload.entityType}${key}`][entityId]
          return state
        }

        if (!state[entityId]) {
          return state
        }

        const { [entityId]: entry, ...remainingEntries } = state
        return remainingEntries
      }

      case ServerActionTypes.ENTITY_REMOVE_SUBSCRIPTION_IDENTIFIERS: {
        const { entityId, subscriptionIdentifiers } = action.payload
        const existing = state[entityId]

        if (!existing) {
          return state
        }

        const updatedSubscriptionIdentifiers = existing.lastUpdate.subscriptionIdentifiers.filter(
          subscriptionIdentifier => !subscriptionIdentifiers.includes(subscriptionIdentifier)
        )

        if (updatedSubscriptionIdentifiers.length === 0) {
          const { [entityId]: entry, ...remainingEntries } = state
          return remainingEntries
        } else {
          return {
            ...state,
            [entityId]: {
              ...existing,
              lastUpdate: {
                ...existing.lastUpdate,
                subscriptionIdentifiers: updatedSubscriptionIdentifiers
              }
            }
          }
        }
      }

      case RepositoryActionTypes.REPOSITORY_UPDATE_FIELD: {
        const { entityType, entityId } = action.payload
        const entry = state[entityId]
        const fieldModifiedAction = entityType === EntityType.NEGOTIATION ? this.getFieldModifiedAction(action) : null

        if (entry) {
          return {
            ...state,
            [entityId]: {
              ...entry,
              fieldUpdates: fieldModifiedAction ? [...entry.fieldUpdates, action, fieldModifiedAction] : [...entry.fieldUpdates, action]
            }
          }
        } else {
          throw new Error(`${entityType} [${entityId}] was not found in the repository`)
        }
      }

      case RepositoryActionTypes.REPOSITORY_UPDATE_META: {
        const { entityType, entityId } = action.payload
        const entry = state[entityId]

        if (entry) {
          return {
            ...state,
            [entityId]: {
              ...entry,
              metaUpdates: [...entry.metaUpdates, action]
            }
          }
        } else {
          throw new Error(`${entityType} [${entityId}] was not found in the repository`)
        }
      }

      case RepositoryActionTypes.REPOSITORY_REVERT_UPDATES: {
        const { entityType, entityId } = action.payload
        const entry = state[entityId]

        if (entry) {
          if (entry.fieldUpdates.length > 0) {
            return {
              ...state,
              [entityId]: {
                ...entry,
                fieldUpdates: []
              }
            }
          } else {
            return state
          }
        } else {
          // throw new Error(`${entityType} [${entityId}] was not found in the repository`)
          // TODO This is throwing an error on occasion when open fin window is closed - why? Removing this throw.
          console.error(`${entityType} [${entityId}] was not found in the repository`)
          return state
        }
      }

      case ServerActionTypes.EVENT_RESPONSE: {
        const { correlationId } = action.payload

        const hasUpdates = Object.values(state).some(s => s.fieldUpdates.some(f => f.payload.correlationId === correlationId))

        if (!hasUpdates) {
          return state
        }

        return Object.entries(state).reduce<Record<string, RepositoryEntry<EntityUpdateUnion>>>(
          (previousValue, [entityId, entityUpdate]) => {
            const newFieldUpdates = entityUpdate.fieldUpdates.filter(fieldUpdate => fieldUpdate.payload.correlationId !== correlationId)
            previousValue[entityId] = { ...entityUpdate, fieldUpdates: newFieldUpdates }
            return previousValue
          },
          {}
        )
      }

      default: {
        throw new Error(`Unexpected action received [${action.type}]`)
      }
    }
  }

  // TODO - it would be better if this function was provided for each entity type
  private shouldClearNonPersistentMeta(existing: EntityUpdateUnion, update: EntityUpdateUnion): boolean {
    if (existing.entityType === EntityType.NEGOTIATION && update.entityType === EntityType.NEGOTIATION) {
      return isMajorVersionBump(existing.entity.version, update.entity.version)
    } else {
      return true
    }
  }

  private getFieldModifiedAction(action: RepositoryActionUpdateField): RepositoryActionUpdateField {
    const modifiedFieldName = action.payload.fieldName
      .split('.')
      .slice(0, -1)
      .concat('modified')
      .join('.')

    return {
      ...action,
      payload: {
        ...action.payload,
        fieldName: modifiedFieldName,
        fieldValue: true
      }
    }
  }
}
